import { type FC, type ReactNode, Suspense, useContext, useCallback } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { Spinner } from 'dibs-elements/exports/Spinner.tsx';

import Error from './Error.tsx';
import MessageSent from './MessageSent.tsx';
import ContactSellerMessageSent from './ContactSellerMessageSent.tsx';
import IntroStep from './IntroStep.tsx';
import OrdersList from './OrdersList.tsx';
import OtherAssistance from './OtherAssistance.tsx';
import SelectedOrder from './SelectedOrder.tsx';
import Topic from './Topic.tsx';
import CheckoutAssistance from './CheckoutAssistance.tsx';
import IntroStepSeller from './IntroStepSeller.tsx';
import ItemsList from './ItemsList.tsx';
import SelectedListing from './SelectedListing.tsx';

import GetHelpContext from './GetHelpContext.ts';
import {
    STEP_INTRO,
    STEP_OTHER_ASSISTANCE,
    STEP_ORDERS_LIST,
    STEP_SELECTED_ORDER,
    STEP_TOPIC,
    STEP_MESSAGE_SENT,
    STEP_CONTACT_SELLER_MESSAGE_SENT,
    STEP_ERROR,
    STEP_CHECKOUT_ASSISTANCE,
    STEP_ITEMS_LIST,
    STEP_SELECTED_LISTING,
} from './constants.ts';

import { type GetHelpRendererQuery } from './__generated__/GetHelpRendererQuery.graphql.ts';

import styles from './styles/GetHelpRenderer.module.css';

const GetHelpRenderer: FC = () => {
    const {
        state: { step, orderId, helpType, topicValue, userId, isSeller, sellerId, itemPk },
    } = useContext(GetHelpContext);

    const { viewer } = useLazyLoadQuery<GetHelpRendererQuery>(
        graphql`
            query GetHelpRendererQuery(
                $orderId: String
                $helpType: GetHelpTopicsHelpType
                $transactionId: ID!
                $hasOrderId: Boolean!
                $topicKey: String!
                $userId: String!
                $hasUserId: Boolean!
                $isLoggedOutUser: Boolean!
                $sellerId: String!
                $hasSellerId: Boolean!
                $isSeller: Boolean!
                $itemId: String!
                $hasItemId: Boolean!
                $userCountryCode: String = ""
            ) {
                viewer {
                    ...IntroStep_viewer
                    ...IntroStepSeller_viewer
                    ...SelectedOrder_viewer @include(if: $hasOrderId)
                    ...OtherAssistance_viewer
                    ...Topic_viewer
                    ...CheckoutAssistance_viewer @skip(if: $isSeller)
                    ...SelectedListing_viewer @include(if: $hasItemId)
                }
            }
        `,
        {
            orderId,
            helpType,
            transactionId: orderId || '',
            hasOrderId: !!orderId,
            topicKey: topicValue || '',
            userId: userId || '',
            hasUserId: !!userId,
            isLoggedOutUser: !userId && !sellerId,
            sellerId: sellerId || '',
            hasSellerId: !!sellerId,
            isSeller,
            itemId: itemPk || '',
            hasItemId: !!itemPk,
        }
    );

    const getContent = useCallback((): ReactNode => {
        switch (step) {
            case STEP_INTRO:
                return isSeller ? (
                    <IntroStepSeller viewer={viewer} />
                ) : (
                    <IntroStep viewer={viewer} />
                );
            case STEP_ORDERS_LIST:
                return <OrdersList />;
            case STEP_ITEMS_LIST:
                return <ItemsList />;
            case STEP_SELECTED_LISTING:
                return <SelectedListing viewer={viewer} />;
            case STEP_OTHER_ASSISTANCE:
                return <OtherAssistance viewer={viewer} />;
            case STEP_SELECTED_ORDER:
                return <SelectedOrder viewer={viewer} />;
            case STEP_TOPIC:
                return <Topic viewer={viewer} />;
            case STEP_MESSAGE_SENT:
                return <MessageSent />;
            case STEP_CONTACT_SELLER_MESSAGE_SENT:
                return <ContactSellerMessageSent />;
            case STEP_CHECKOUT_ASSISTANCE:
                return <CheckoutAssistance viewer={viewer} />;
            case STEP_ERROR:
                return <Error />;
            default:
                return isSeller ? (
                    <IntroStepSeller viewer={viewer} />
                ) : (
                    <IntroStep viewer={viewer} />
                );
        }
    }, [step, viewer, isSeller]);

    return (
        <Suspense
            fallback={
                <div className={styles.spinnerWrapper}>
                    <Spinner />
                </div>
            }
        >
            {getContent()}
        </Suspense>
    );
};

export default GetHelpRenderer;
